import React from 'react';
import { Helmet } from 'react-helmet-async';

export const PrivacyPolicy = () => {
  return (
    <div className="pt-20">
      <Helmet>
        <title>Privacy Policy | Trademark Infinite - Protecting Your Personal Information</title>
        <meta name="description" content="Read our privacy policy to understand how Trademark Infinite collects, uses, and protects your personal information when using our trademark services." />
      </Helmet>
      <section className="bg-gradient-to-br from-red-600 to-violet-600 text-white py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold text-center">Privacy Policy</h1>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 mb-8">Last Updated: January 2026</p>

            <div className="space-y-8 text-gray-700">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
                <p>
                  Welcome to Trademark Infinite ("we," "our," or "us"). We respect your privacy and are committed to protecting your personal data. This privacy policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
                <p className="mb-4">We collect several types of information from and about users of our services:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Personal Information:</strong> Name, email address, phone number, mailing address, and payment information when you register for our services or make inquiries.</li>
                  <li><strong>Business Information:</strong> Company name, trademark details, business structure, and related intellectual property information necessary to provide our services.</li>
                  <li><strong>Usage Data:</strong> Information about how you access and use our website, including your IP address, browser type, pages visited, time spent on pages, and other diagnostic data.</li>
                  <li><strong>Cookies and Tracking Technologies:</strong> We use cookies and similar tracking technologies to track activity on our service and hold certain information.</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
                <p className="mb-4">We use the information we collect for various purposes:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>To provide and maintain our trademark and intellectual property services</li>
                  <li>To process your transactions and manage your account</li>
                  <li>To communicate with you about your services, including updates and administrative information</li>
                  <li>To respond to your inquiries and provide customer support</li>
                  <li>To improve our website and services through analysis of usage patterns</li>
                  <li>To send you marketing communications (with your consent where required)</li>
                  <li>To detect, prevent, and address technical issues and fraudulent activity</li>
                  <li>To comply with legal obligations and protect our legal rights</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Information Sharing and Disclosure</h2>
                <p className="mb-4">We may share your information in the following circumstances:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Service Providers:</strong> We may share your information with third-party service providers who perform services on our behalf, such as payment processing, data analysis, and customer service.</li>
                  <li><strong>Legal Requirements:</strong> We may disclose your information if required to do so by law or in response to valid requests by public authorities.</li>
                  <li><strong>Business Transfers:</strong> If we are involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.</li>
                  <li><strong>With Your Consent:</strong> We may share your information with third parties when you give us explicit consent to do so.</li>
                </ul>
                <p className="mt-4">We do not sell, rent, or trade your personal information to third parties for marketing purposes.</p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Security</h2>
                <p>
                  We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Your Privacy Rights</h2>
                <p className="mb-4">Depending on your location, you may have the following rights regarding your personal information:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>The right to access and receive a copy of your personal information</li>
                  <li>The right to rectify inaccurate personal information</li>
                  <li>The right to request deletion of your personal information</li>
                  <li>The right to restrict or object to our processing of your personal information</li>
                  <li>The right to data portability</li>
                  <li>The right to withdraw consent at any time (where processing is based on consent)</li>
                </ul>
                <p className="mt-4">To exercise these rights, please contact us using the information provided below.</p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Data Retention</h2>
                <p>
                  We retain your personal information for as long as necessary to fulfill the purposes outlined in this privacy policy, unless a longer retention period is required or permitted by law. When we no longer need your information, we will securely delete or anonymize it.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Children's Privacy</h2>
                <p>
                  Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you are a parent or guardian and believe your child has provided us with personal information, please contact us.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Third-Party Links</h2>
                <p>
                  Our website may contain links to third-party websites that are not operated by us. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites or services. We encourage you to review the privacy policy of every site you visit.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Changes to This Privacy Policy</h2>
                <p>
                  We may update our privacy policy from time to time. We will notify you of any changes by posting the new privacy policy on this page and updating the "Last Updated" date. You are advised to review this privacy policy periodically for any changes.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Contact Us</h2>
                <p className="mb-4">If you have any questions about this privacy policy or our privacy practices, please contact us at:</p>
                <div className="bg-gray-50 rounded-lg p-6">
                  <p className="font-semibold text-gray-900">Trademark Infinite</p>
                  <p>Email: info@trademarkinfinite.com</p>
                  <p>Phone: +1 (888) 201-6668</p>
                  <p>Address: One Wilshire Building, 624 S Grand Ave Floor 21, Los Angeles, CA 90017</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};